import {
  createContext,
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { InfoBlock } from '@smartshell/ui-kit';
import cn from 'classnames';
import { Outlet, useLocation } from 'react-router-dom';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import { useNotCompleted } from 'modals/ClientRegisterModal/NotComletedContext';
import { News } from 'news';
import { MapConflictContext } from 'pages/HostsMap/MapConflictContext';
import { isSandbox } from 'sandbox';
import { OnboardingTour } from 'sandbox/onboarding';

import { LoadableNotification } from '../LoadableNotification';
import { ReleaseChecker } from '../ReleaseChecker';
import { Header } from './Header';
import s from './Layout.module.scss';
import { NavBar } from './NavBar';
import { NavBarMobile } from './NavBarMobile';
import { NotCompletedRegister } from './NotCompletedRegister';
import { WorkShiftChecker } from './WorkShiftChecker';
export const LayoutContext = createContext<HTMLElement | undefined>(undefined);

export const Layout: FunctionComponent = () => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { tokens },
  } = useContext(AuthContext);
  const encodedToken = atob(tokens?.access_token?.split('.')[1] ?? '');
  const clubId = encodedToken ? JSON.parse(encodedToken)?.company_id : null;

  const { notCompletedRegisters, clearNotCompletedRegisters } =
    useNotCompleted() as NonNullable<ReturnType<typeof useNotCompleted>>;

  const layoutRef = useRef<HTMLElement>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const location = useLocation();
  const isMapPage = location.pathname.includes('/map');

  useEffect(() => {
    clearNotCompletedRegisters();
  }, [clubId]);

  const [hasConflict, setHasConflict] = useState(false);

  return (
    <MapConflictContext.Provider value={{ hasConflict, setHasConflict }}>
      <News>
        <div className={s.container}>
          <Header />
          <div className={s['content-container']}>
            <NavBarMobile
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />
            <div className={s.navBar}>
              <NavBar />
            </div>
            <main
              ref={layoutRef}
              id="main-content"
              data-onboarding="page-content"
              className={cn(s.content, isMapPage && s.content_iMapPage)}
            >
              <LayoutContext.Provider value={layoutRef.current ?? undefined}>
                <div className={s.notifications}>
                  <ReleaseChecker />
                  <LoadableNotification direction="row" closable />
                  <WorkShiftChecker />
                  {isMapPage && hasConflict && (
                    <InfoBlock
                      color="orange"
                      message={t('host.map.conflict')}
                    />
                  )}
                  {notCompletedRegisters.map((client, index) => (
                    <NotCompletedRegister key={index} client={client} />
                  ))}
                </div>
                <Outlet />
              </LayoutContext.Provider>
            </main>
          </div>
          {isSandbox && <OnboardingTour />}
        </div>
      </News>
    </MapConflictContext.Provider>
  );
};
